import React, { useState, useEffect, useCallback } from 'react';
import {
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
    VStack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    SimpleGrid,
    FormControl,
    FormLabel,
    Tooltip,
} from '@chakra-ui/react';
import {
    AiOutlineSetting,
    AiOutlinePlus,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineIdcard,
    AiOutlineSend,
    AiOutlineSchedule,
    AiOutlineCheckCircle,
    AiOutlineBell,
    AiOutlineDatabase,
    AiOutlineTrophy,
    AiOutlineMinus,
} from 'react-icons/ai';
import api from '../../../services/api';
import ConfirmDeleteDialog from '../../../components/UI/ConfirmDeleteDialog';
import EmManutencao from '../../../components/UI/EmManutencao';
import { useModalMinimizer } from '../../../contexts/ModalMinimizerContext';
// ⚠️ Cobranças "Em breve" (pedido do Lucas em 16/07/2026): a aba de configuração
// foi trocada pelo aviso padrão enquanto o sistema não é usado da forma correta.
// Para reativar, importe CobrancasControlador de './CobrancasControlador' e
// renderize-o novamente no lugar do <EmManutencao /> abaixo.

const AREAS = [
    { key: 'dropdowns', label: 'Listas & Dropdowns', icon: AiOutlineDatabase },
    { key: 'cobrancas', label: 'Cobranças do Comercial', icon: AiOutlineBell },
];

// Só status_atividade e resultados_negocio têm STATUS DO DROPDOWN (role): a lógica
// do sistema (concluída/cancelada, ganho/perdido) lê o papel, nunca o nome. O
// vocabulário é padronizado (Positivo/Negativo/Neutro); `roleHint` explica o efeito
// em cada tela. Os demais tipos são sempre neutros (sem status).
const DROPDOWN_TYPES = [
    { key: 'cargos', label: 'Cargos', icon: AiOutlineIdcard, color: '#4299E1' },
    { key: 'canais', label: 'Canais de Origem', icon: AiOutlineSend, color: '#02EA75' },
    { key: 'tipos_atividade', label: 'Tipos de Atividade', icon: AiOutlineSchedule, color: '#9F7AEA' },
    {
        key: 'status_atividade', label: 'Status de Atividade', icon: AiOutlineCheckCircle, color: '#ECC94B',
        hasRole: true,
        roleHint: 'Positivo = atividade concluída · Negativo = cancelada · Neutro = em andamento. Define os contadores, Metas e Relatórios — o nome é livre.',
    },
    {
        key: 'resultados_negocio', label: 'Resultados de Negócio', icon: AiOutlineTrophy, color: '#F56565',
        hasRole: true,
        roleHint: 'Positivo = ganho · Negativo = perdido · Neutro = só encerra (ex.: Em espera). Define a classificação em Metas e Relatórios — o nome é livre.',
    },
];

// Status do dropdown (role_CONTROLADORES_COMERCIAL_NOGLE): vocabulário padronizado.
// 0 = Neutro | 1 = Positivo | 2 = Negativo (mesmos nomes em todas as telas).
const ROLE_META = {
    0: { label: 'Neutro', cor: '#718096' },
    1: { label: 'Positivo', cor: '#02EA75' },
    2: { label: 'Negativo', cor: '#FC8181' },
};

const PALETA_CORES = [
    '#9F7AEA', '#4299E1', '#ECC94B', '#ED8936', '#FC8181',
    '#02EA75', '#718096', '#F687B3', '#76E4F7', '#FBD38D',
    '#68D391', '#48BB78', '#63B3ED', '#B794F4', '#A0AEC0',
    '#FC8181', '#F6AD55', '#CBD5E0', '#FEB2B2', '#E9D8FD',
];

const ColorPicker = ({ value, onChange }) => (
    <Flex wrap="wrap" gap={2} mt={2}>
        {PALETA_CORES.map(cor => (
            <Box
                key={cor}
                w="22px" h="22px"
                borderRadius="md"
                bg={cor}
                cursor="pointer"
                border="2px solid"
                borderColor={value === cor ? 'white' : 'transparent'}
                boxShadow={value === cor ? `0 0 0 1px ${cor}` : 'none'}
                _hover={{ transform: 'scale(1.25)', boxShadow: `0 0 6px ${cor}80` }}
                transition="all 0.15s"
                onClick={() => onChange(cor)}
            />
        ))}
    </Flex>
);

const Controladores = () => {
    const toast = useToast();
    const [area, setArea] = useState('dropdowns');
    const [activeTab, setActiveTab] = useState(0);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Modal Criar/Editar
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editOption, setEditOption] = useState(null);
    const [formValue, setFormValue] = useState('');
    const [formColor, setFormColor] = useState('#4299E1');
    const [formRole, setFormRole] = useState(0); // papel (só p/ tipos com hasRole)
    
    // Modal Excluir (com verificação de uso: avisa quando a opção está referenciada no CRM)
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();
    const [deleteOption, setDeleteOption] = useState(null);
    const [checandoUso, setChecandoUso] = useState(false);
    const [usoDelete, setUsoDelete] = useState(null); // { total, detalhe } | total -1 = falha na verificação

    const activeType = DROPDOWN_TYPES[activeTab];

    // Conta onde a opção está sendo usada, consultando os registros reais do CRM.
    // Compara por id (coluna gravada) E por nome (valor já resolvido pela API).
    const contarUso = useCallback(async (typeKey, opt) => {
        const eqId = (a, b) => String(a ?? '') === String(b ?? '');
        const eqNome = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
        if (typeKey === 'cargos') {
            const { data } = await api.get('/comercial/contatos');
            const n = (data || []).filter(c => eqId(c.cargo_id, opt.id) || eqNome(c.cargo, opt.nome)).length;
            return { total: n, detalhe: `${n} contato(s)` };
        }
        if (typeKey === 'canais') {
            const [cards, contatos] = await Promise.all([
                api.get('/comercial/pipeline-cards'),
                api.get('/comercial/contatos'),
            ]);
            const nCards = (cards.data || []).filter(c => eqNome(c.canal, opt.nome) || eqId(c.canal, opt.id)).length;
            const nContatos = (contatos.data || []).filter(c => eqId(c.source_channel_id, opt.id) || eqNome(c.canal, opt.nome)).length;
            const partes = [];
            if (nCards) partes.push(`${nCards} negócio(s)`);
            if (nContatos) partes.push(`${nContatos} contato(s)`);
            return { total: nCards + nContatos, detalhe: partes.join(' e ') || '0 registro(s)' };
        }
        if (typeKey === 'tipos_atividade' || typeKey === 'status_atividade') {
            const { data } = await api.get('/comercial/atividades');
            const n = (data || []).filter(a => typeKey === 'tipos_atividade'
                ? (eqId(a.tipo_id, opt.id) || eqNome(a.tipo, opt.nome))
                : (eqId(a.status_id, opt.id) || eqNome(a.status, opt.nome))
            ).length;
            return { total: n, detalhe: `${n} atividade(s)` };
        }
        if (typeKey === 'resultados_negocio') {
            // Referência gravada em id_result dos negócios concluídos (desde 2026-07-17).
            const { data } = await api.get('/comercial/pipeline-cards');
            const n = (data || []).filter(c => eqId(c.result_id, opt.id)).length;
            return { total: n, detalhe: `${n} negócio(s)` };
        }
        return { total: 0, detalhe: '' };
    }, []);

    const handleAbrirExcluir = useCallback(async (opt) => {
        setDeleteOption(opt);
        setUsoDelete(null);
        setChecandoUso(true);
        onDelOpen();
        try {
            const uso = await contarUso(activeType.key, opt);
            setUsoDelete(uso);
        } catch (err) {
            console.error('Erro ao verificar uso da opção:', err);
            setUsoDelete({ total: -1, detalhe: '' });
        } finally {
            setChecandoUso(false);
        }
    }, [activeType.key, contarUso, onDelOpen]);

    const descricaoDelete = checandoUso
        ? 'Verificando onde esta opção está em uso...'
        : usoDelete && usoDelete.total > 0
            ? `ATENÇÃO: esta opção está EM USO em ${usoDelete.detalhe}. Excluir pode deixar esses registros com referência quebrada no CRM.`
            : usoDelete && usoDelete.total === 0
                ? 'Nenhum registro do CRM usa esta opção — remoção segura.'
                : 'Isto removerá a opção das listagens de seleção e pode quebrar referências históricas.';

    const fetchOptions = useCallback(async (typeKey) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/comercial/dropdowns/${typeKey}`);
            setOptions(data || []);
        } catch (err) {
            console.error(`Erro ao carregar opções de ${typeKey}:`, err);
            toast({
                title: 'Erro ao carregar',
                description: 'Não foi possível buscar as opções no banco.',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchOptions(activeType.key);
    }, [activeTab, activeType.key, fetchOptions]);

    const { minimizeModal, removeMinimizedModal, consumePendingRestore } = useModalMinimizer();

    const handleOpenModal = (option = null) => {
        setEditOption(option);
        setFormValue(option ? option.nome : '');
        setFormColor(option ? option.cor || '#4299E1' : '#4299E1');
        setFormRole(option ? Number(option.role) || 0 : 0);
        onOpen();
    };

    const handleMinimizar = useCallback(() => {
        onClose();
        const modalId = 'controlador-modal';
        const title = editOption ? 'Editar Opção' : `Nova Opção em ${activeType.label}`;
        const subtitle = formValue || activeType.label;
        minimizeModal({
            id: modalId,
            title,
            subtitle,
            icon: activeType.icon || AiOutlineSetting,
            route: '/comercial/controladores',
            formData: { editOption, formValue, formColor, formRole },
            onExpand: () => {
                onOpen();
            },
            onClose: () => {
                setEditOption(null);
                setFormValue('');
            }
        });
    }, [onClose, onOpen, editOption, activeType, formValue, formColor, formRole, minimizeModal]);

    useEffect(() => {
        const pendingData = consumePendingRestore('controlador-modal');
        if (pendingData) {
            if (pendingData.editOption !== undefined) setEditOption(pendingData.editOption);
            if (pendingData.formValue !== undefined) setFormValue(pendingData.formValue);
            if (pendingData.formColor !== undefined) setFormColor(pendingData.formColor);
            if (pendingData.formRole !== undefined) setFormRole(pendingData.formRole);
            onOpen();
        }
    }, [consumePendingRestore, onOpen]);

    const handleCloseModal = useCallback(() => {
        setEditOption(null);
        setFormValue('');
        removeMinimizedModal('controlador-modal');
        onClose();
    }, [onClose, removeMinimizedModal]);

    const handleSave = async () => {
        if (!formValue.trim()) {
            toast({ title: 'O valor não pode ser vazio', status: 'warning', duration: 2000 });
            return;
        }
        try {
            const payload = { nome: formValue.trim() };
            if (activeType.key !== 'cargos') {
                payload.cor = formColor;
            }
            // Papel só é enviado para os tipos com lógica (status/resultados).
            if (activeType.hasRole) {
                payload.role = formRole;
            }
            if (editOption) {
                await api.put(`/comercial/dropdowns/${activeType.key}/${editOption.id}`, payload);
                toast({ title: 'Opção atualizada com sucesso', status: 'success', duration: 2000 });
            } else {
                await api.post(`/comercial/dropdowns/${activeType.key}`, payload);
                toast({ title: 'Opção cadastrada com sucesso', status: 'success', duration: 2000 });
            }
            handleCloseModal();
            fetchOptions(activeType.key);
        } catch (err) {
            console.error('Erro ao salvar opção:', err);
            toast({ title: 'Erro ao salvar', status: 'error', duration: 3000 });
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteOption) return;
        try {
            await api.delete(`/comercial/dropdowns/${activeType.key}/${deleteOption.id}`);
            toast({ title: 'Opção removida com sucesso', status: 'info', duration: 2000 });
            onDelClose();
            setDeleteOption(null);
            fetchOptions(activeType.key);
        } catch (err) {
            console.error('Erro ao deletar opção:', err);
            toast({ title: 'Erro ao deletar', status: 'error', duration: 3000 });
        }
    };

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header com Gradiente Premium */}
            <Box
                p={6}
                borderRadius="2xl"
                bgGradient="linear(135deg, surface, surfaceRaised)"
                border="1px solid"
                borderColor="borderPrimary"
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top="-100px"
                    right="-100px"
                    w="300px"
                    h="300px"
                    bg="radial-gradient(circle, rgba(2,234,117,0.1) 0%, transparent 70%)"
                    pointerEvents="none"
                />
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                    <HStack spacing={4}>
                        <Flex
                            w="50px"
                            h="50px"
                            borderRadius="xl"
                            bg="linear-gradient(135deg, rgba(2,234,117,0.15) 0%, rgba(66,153,225,0.15) 100%)"
                            border="1px solid"
                            borderColor="rgba(2,234,117,0.3)"
                            align="center"
                            justify="center"
                        >
                            <Icon as={AiOutlineSetting} color="secondary" boxSize="26px" />
                        </Flex>
                        <VStack align="start" spacing={0}>
                            <Heading size="lg" color="textPrimary" fontWeight="900">Controladores</Heading>
                        </VStack>
                    </HStack>
                    {area === 'dropdowns' && (
                        <Button
                            leftIcon={<AiOutlinePlus />}
                            size="sm"
                            bg="secondary"
                            color="primary"
                            _hover={{ bg: 'secondaryHover' }}
                            fontWeight="800"
                            onClick={() => handleOpenModal()}
                        >
                            Adicionar Opção
                        </Button>
                    )}
                </Flex>
            </Box>

            {/* Seletor de Área: Dropdowns x Cobranças */}
            <HStack
                bg="surface"
                border="1px solid"
                borderColor="borderPrimary"
                borderRadius="xl"
                p={1}
                spacing={1}
                alignSelf="flex-start"
                flexWrap="wrap"
            >
                {AREAS.map(a => {
                    const active = area === a.key;
                    return (
                        <Flex
                            key={a.key}
                            as="button"
                            align="center"
                            gap={2}
                            px={4}
                            py={2}
                            borderRadius="lg"
                            bg={active ? 'rgba(2,234,117,0.12)' : 'transparent'}
                            color={active ? 'secondary' : 'textSecondary'}
                            fontWeight={active ? '800' : '600'}
                            fontSize="sm"
                            transition="all 0.15s"
                            _hover={{ bg: active ? 'rgba(2,234,117,0.12)' : 'surfaceHover', color: active ? 'secondary' : 'textPrimary' }}
                            onClick={() => setArea(a.key)}
                        >
                            <Icon as={a.icon} boxSize="16px" />
                            {a.label}
                        </Flex>
                    );
                })}
            </HStack>

            {area === 'cobrancas' && (
                <EmManutencao
                    titulo="Cobranças do Comercial"
                    descricao="O sistema de cobranças automáticas (sininho, toasts e regras) está em produção e será liberado em breve."
                    recursos={[
                        { icon: AiOutlineBell, title: 'Sininho', desc: 'Notificações de pendências' },
                        { icon: AiOutlineSetting, title: 'Regras', desc: 'Configuração por usuário' },
                    ]}
                />
            )}

            {area === 'dropdowns' && (
              <>
            {/* Painel Estatístico Rápido */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                {DROPDOWN_TYPES.map((dt, idx) => (
                    <Box
                        key={dt.key}
                        bg="surface"
                        border="1px solid"
                        borderColor={activeTab === idx ? dt.color : 'borderPrimary'}
                        borderRadius="xl"
                        p={4}
                        cursor="pointer"
                        onClick={() => setActiveTab(idx)}
                        _hover={{ bg: 'surfaceHover', transition: 'all 0.2s' }}
                        transition="all 0.2s"
                        boxShadow={activeTab === idx ? `0 0 15px ${dt.color}15` : 'none'}
                    >
                        <HStack justify="space-between" mb={2}>
                            <Icon as={dt.icon} color={dt.color} boxSize="20px" />
                            <Box w="6px" h="6px" borderRadius="full" bg={activeTab === idx ? dt.color : 'transparent'} />
                        </HStack>
                        <Text fontSize="sm" fontWeight="800" color="textPrimary">{dt.label}</Text>
                        <Text fontSize="xs" color="textMuted">Clique para calibrar</Text>
                    </Box>
                ))}
            </SimpleGrid>

            {/* Tabela de Gerenciamento das Opções */}
            <Tabs index={activeTab} onChange={(index) => setActiveTab(index)} variant="unstyled">
                <TabList display="none">
                    {DROPDOWN_TYPES.map(dt => (
                        <Tab key={dt.key}>{dt.label}</Tab>
                    ))}
                </TabList>
                
                <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden" mt={2}>
                    <Flex
                        align="center"
                        gap={3}
                        p={4}
                        bg="surfaceRaised"
                        borderBottom="1px solid"
                        borderColor="borderPrimary"
                    >
                        <Icon as={activeType.icon} color="secondary" boxSize="20px" />
                        <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800">
                            {activeType.label} Cadastrados
                        </Heading>
                        {loading && <Spinner size="xs" color="secondary" ml={2} />}
                    </Flex>
                    
                    <TableContainer>
                        <Table variant="simple" size="sm">
                            <Thead bg="blackAlpha.200">
                                <Tr>
                                    <Th color="textMuted" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" w="100px">ID</Th>
                                    <Th color="textMuted" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Nome / Valor no Dropdown</Th>
                                    {activeType.hasRole && (
                                        <Th color="textMuted" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Status do Dropdown</Th>
                                    )}
                                    <Th color="textMuted" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading && options.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={activeType.hasRole ? 4 : 3} textAlign="center" py={8} borderColor="borderPrimary">
                                            <Spinner size="md" color="secondary" />
                                        </Td>
                                    </Tr>
                                ) : options.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={activeType.hasRole ? 4 : 3} textAlign="center" py={8} color="textMuted" borderColor="borderPrimary">
                                            Nenhuma opção cadastrada para esta categoria.
                                        </Td>
                                    </Tr>
                                ) : (
                                    options.map((opt) => (
                                        <Tr key={opt.id} _hover={{ bg: 'surfaceHover' }}>
                                            <Td borderColor="borderPrimary" color="textMuted" fontWeight="700" fontSize="xs">
                                                #{opt.id}
                                            </Td>
                                            <Td borderColor="borderPrimary" color="textPrimary" fontWeight="700">
                                                <HStack spacing={2}>
                                                    {opt.cor && (
                                                        <Box w="10px" h="10px" borderRadius="full" bg={opt.cor} flexShrink={0} />
                                                    )}
                                                    <Text>{opt.nome}</Text>
                                                </HStack>
                                            </Td>
                                            {activeType.hasRole && (
                                                <Td borderColor="borderPrimary">
                                                    <Badge
                                                        bg={`${ROLE_META[Number(opt.role) || 0].cor}22`}
                                                        color={ROLE_META[Number(opt.role) || 0].cor}
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        borderRadius="md"
                                                        px={2}
                                                        py={0.5}
                                                    >
                                                        {ROLE_META[Number(opt.role) || 0].label}
                                                    </Badge>
                                                </Td>
                                            )}
                                            <Td borderColor="borderPrimary" isNumeric>
                                                <HStack spacing={1} justify="flex-end">
                                                    <IconButton
                                                        aria-label="Editar"
                                                        icon={<AiOutlineEdit />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textSecondary"
                                                        _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                                        onClick={() => handleOpenModal(opt)}
                                                    />
                                                    <IconButton
                                                        aria-label="Excluir"
                                                        icon={<AiOutlineDelete />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textMuted"
                                                        _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                        onClick={() => handleAbrirExcluir(opt)}
                                                    />
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>
            </Tabs>
              </>
            )}

            {/* Modal Criar/Editar */}
            <Modal isOpen={isOpen} onClose={handleMinimizar} isCentered size="md" closeOnOverlayClick={true}>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="xl">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pr="90px">
                        <HStack spacing={3}>
                            <Icon as={activeType.icon} color="secondary" boxSize="20px" />
                            <Text fontWeight="800">{editOption ? 'Editar Opção' : `Nova Opção em ${activeType.label}`}</Text>
                        </HStack>
                    </ModalHeader>
                    <HStack position="absolute" top={4} right={4} spacing={1} zIndex={10}>
                        <IconButton
                            aria-label="Minimizar"
                            title="Minimizar"
                            icon={<Icon as={AiOutlineMinus} boxSize="16px" />}
                            size="sm"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: "whiteAlpha.200", color: "secondary" }}
                            borderRadius="md"
                            onClick={handleMinimizar}
                        />
                        <ModalCloseButton position="relative" top={0} right={0} color="textSecondary" onClick={handleCloseModal} />
                    </HStack>
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Valor no Dropdown
                                </FormLabel>
                                <Input
                                    value={formValue}
                                    onChange={e => setFormValue(e.target.value)}
                                    placeholder="Ex: Novo Valor..."
                                    bg="surfaceRaised"
                                    borderColor="gray.600"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                            </FormControl>
                            {activeType.key !== 'cargos' && (
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Cor de Exibição
                                    </FormLabel>
                                    <Box px={1} pt={1} bg="surfaceRaised" border="1px solid" borderColor={formColor} borderRadius="lg" p={3}>
                                        <HStack spacing={2} mb={3}>
                                            <Box w="16px" h="16px" borderRadius="full" bg={formColor} />
                                            <Text fontSize="xs" color="textPrimary" fontWeight="700">{formColor}</Text>
                                        </HStack>
                                        <ColorPicker value={formColor} onChange={setFormColor} />
                                    </Box>
                                </FormControl>
                            )}
                            {activeType.hasRole && (
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Status do Dropdown
                                    </FormLabel>
                                    <HStack spacing={2}>
                                        {[1, 2, 0].map(r => {
                                            const ativo = formRole === r;
                                            const cor = ROLE_META[r].cor;
                                            return (
                                                <Box
                                                    key={r}
                                                    flex={1}
                                                    px={2} py={2}
                                                    textAlign="center"
                                                    borderRadius="lg"
                                                    cursor="pointer"
                                                    bg={ativo ? `${cor}25` : 'surfaceRaised'}
                                                    border="1px solid"
                                                    borderColor={ativo ? cor : 'borderPrimary'}
                                                    color={ativo ? cor : 'textMuted'}
                                                    fontSize="xs"
                                                    fontWeight="800"
                                                    transition="all 0.15s"
                                                    _hover={{ borderColor: cor, color: cor }}
                                                    onClick={() => setFormRole(r)}
                                                >
                                                    {ROLE_META[r].label}
                                                </Box>
                                            );
                                        })}
                                    </HStack>
                                    <Text fontSize="11px" color="textMuted" mt={2} lineHeight="1.4">
                                        {activeType.roleHint}
                                    </Text>
                                </FormControl>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: 'surfaceHover' }} onClick={onClose} size="sm">
                            Cancelar
                        </Button>
                        <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSave} size="sm">
                            {editOption ? 'Salvar Alterações' : 'Adicionar Opção'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirmação padrão de exclusão — a descrição reflete o uso real da opção no CRM */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={() => { onDelClose(); setDeleteOption(null); setUsoDelete(null); }}
                onConfirm={handleConfirmDelete}
                title="Excluir Opção"
                itemName={deleteOption?.nome}
                description={descricaoDelete}
                isLoading={checandoUso}
                warning={!checandoUso && !!usoDelete && usoDelete.total > 0}
            />
        </VStack>
    );
};

export default Controladores;
